import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Store, Package, Tag, Users, Database, Download, Pencil, ShieldCheck, RefreshCw } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editing, setEditing] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [provider, setProvider] = useState(null);
  const [refreshing, setRefreshing] = useState("");

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadPrices = () => api.get("/admin/prices").then((r) => setPrices(r.data)).catch(() => {});

  useEffect(() => {
    loadStats();
    api.get("/admin/stores").then((r) => setStores(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    loadPrices();
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/admin/prices/provider-status").then((r) => setProvider(r.data)).catch(() => {});
  }, []);

  const refreshReal = async (retailerId, label) => {
    setRefreshing(retailerId);
    try {
      const { data } = await api.post(`/admin/prices/refresh?retailer_id=${retailerId}&limit=30`);
      toast.success(`${label} : ${data.products_updated} produits mis à jour (${data.products_skipped} ignorés)`);
      loadPrices();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Actualisation impossible");
    } finally {
      setRefreshing("");
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/prices/${editing.id}`, {
        price: parseFloat(editing.price),
        promo: !!editing.promo,
        promo_price: editing.promo && editing.promo_price ? parseFloat(editing.promo_price) : null,
        validated: true,
      });
      toast.success("Prix mis à jour");
      setEditing(null);
      loadPrices();
      api.get("/admin/audit").then((r) => setAudit(r.data));
    } catch (e) { toast.error("Erreur"); }
  };

  const refreshAll = async () => {
    setRefreshing("all");
    try {
      const { data } = await api.post(`/admin/prices/refresh-all?limit=30`);
      toast.success(`${data.total_updated} produits mis à jour au total`);
      loadPrices();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Actualisation impossible");
    } finally {
      setRefreshing("");
    }
  };

  const importOff = async () => {
    if (!barcode) return;
    try {
      const { data } = await api.post(`/admin/products/import-off/${barcode}`);
      toast.success(`Importé : ${data.product.name}`);
      setBarcode("");
      api.get("/admin/products").then((r) => setProducts(r.data));
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import impossible");
    }
  };

  const statCards = stats ? [
    { icon: Store, label: "Enseignes", value: stats.retailers },
    { icon: Store, label: "Magasins", value: stats.stores },
    { icon: Package, label: "Produits", value: stats.products },
    { icon: Tag, label: "Relevés de prix", value: stats.price_observations },
    { icon: Users, label: "Utilisateurs", value: stats.users },
  ] : [];

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl font-800 tracking-tight text-secondary">Back-office</h1>
        </div>
        <p className="mt-1 text-muted-foreground">Gestion des enseignes, produits, prix et imports.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-5" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-card p-5">
              <s.icon className="h-5 w-5 text-primary" />
              <p className="mt-2 font-display text-2xl font-800 text-secondary">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="prices" className="mt-8">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="prices" data-testid="tab-prices">Prix</TabsTrigger>
            <TabsTrigger value="stores" data-testid="tab-stores">Magasins</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Produits</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Journal</TabsTrigger>
          </TabsList>

          <TabsContent value="prices" className="mt-4">
            <div className="mb-4 rounded-xl border border-border bg-card p-5" data-testid="real-prices-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 font-display font-700 text-secondary">
                    <RefreshCw className="h-4 w-4 text-primary" /> Prix réels (fournisseur payant)
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {provider?.configured
                      ? `Connecté à ${provider.provider}. Récupère les prix réels par produit et remplace les prix démo.`
                      : "Non configuré. Ajoutez PILOTERR_API_KEY dans backend/.env pour activer la récupération des vrais prix."}
                  </p>
                </div>
                <Badge className={provider?.configured ? "bg-primary text-white" : "bg-muted text-muted-foreground"} data-testid="provider-status-badge">
                  {provider?.configured ? "Activé" : "Non configuré"}
                </Badge>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full" disabled={!provider?.configured || refreshing} onClick={() => refreshReal("ret-leclerc", "E.Leclerc")} data-testid="refresh-leclerc-btn">
                  {refreshing === "ret-leclerc" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} E.Leclerc
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" disabled={!provider?.configured || refreshing} onClick={() => refreshReal("ret-carrefour", "Carrefour")} data-testid="refresh-carrefour-btn">
                  {refreshing === "ret-carrefour" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Carrefour
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" disabled={!provider?.configured || refreshing} onClick={() => refreshReal("ret-intermarche", "Intermarché")} data-testid="refresh-intermarche-btn">
                  {refreshing === "ret-intermarche" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Intermarché
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" disabled={!provider?.configured || refreshing} onClick={() => refreshReal("ret-auchan", "Auchan")} data-testid="refresh-auchan-btn">
                  {refreshing === "ret-auchan" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Auchan
                </Button>
                <Button size="sm" className="rounded-full" disabled={!provider?.configured || refreshing} onClick={refreshAll} data-testid="refresh-all-btn">
                  {refreshing === "all" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Tout actualiser
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Actualisation automatique quotidienne activée quand le fournisseur est configuré.</p>
            </div>
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Produit</TableHead><TableHead>Magasin</TableHead><TableHead>Prix</TableHead><TableHead>Promo</TableHead><TableHead>Source</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {prices.slice(0, 100).map((p) => (
                    <TableRow key={p.id} data-testid={`price-row-${p.id}`}>
                      <TableCell className="font-medium">{p.product_name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.store_name}</TableCell>
                      <TableCell className="font-semibold">{euro(p.price)}</TableCell>
                      <TableCell>{p.promo ? <Badge className="bg-accent text-white">{euro(p.promo_price)}</Badge> : "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={p.source === "piloterr" ? "border-primary text-primary" : "text-muted-foreground"}>
                          {p.source === "piloterr" ? "Réel" : p.source === "admin" ? "Admin" : "Démo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.date}</TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...p })} data-testid={`edit-price-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="stores" className="mt-4">
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Magasin</TableHead><TableHead>Enseigne</TableHead><TableHead>Code postal</TableHead><TableHead>Adresse</TableHead></TableRow></TableHeader>
                <TableBody>
                  {stores.map((s) => (
                    <TableRow key={s.id} data-testid={`store-row-${s.id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>{s.retailer_name}</TableCell>
                      <TableCell>{s.postcode}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{s.address}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-5">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Importer depuis Open Food Facts (code-barres)</Label>
                <Input placeholder="ex: 3017620422003" value={barcode} onChange={(e) => setBarcode(e.target.value)} className="mt-1.5" data-testid="off-barcode-input" />
              </div>
              <Button onClick={importOff} className="rounded-full" data-testid="off-import-btn"><Download className="mr-2 h-4 w-4" /> Importer</Button>
            </div>
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Produit</TableHead><TableHead>Marque</TableHead><TableHead>Catégorie</TableHead><TableHead>Code-barres</TableHead></TableRow></TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.brand}</TableCell>
                      <TableCell><Badge variant="secondary">{p.category}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.barcode || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Opérateur</TableHead><TableHead>Ancien prix</TableHead><TableHead>Nouveau prix</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>
                  {audit.map((a) => (
                    <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                      <TableCell className="font-medium">{a.operator}</TableCell>
                      <TableCell>{euro(a.old?.price)}</TableCell>
                      <TableCell className="font-semibold text-primary">{euro(a.new?.price)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(a.at).toLocaleString("fr-FR")}</TableCell>
                    </TableRow>
                  ))}
                  {audit.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Aucune modification enregistrée</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier le prix</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{editing.product_name} · {editing.store_name}</p>
              <div>
                <Label>Prix (€)</Label>
                <Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1.5" data-testid="edit-price-input" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted/60 p-3">
                <Label>En promotion</Label>
                <Switch checked={!!editing.promo} onCheckedChange={(v) => setEditing({ ...editing, promo: v })} data-testid="edit-promo-toggle" />
              </div>
              {editing.promo && (
                <div>
                  <Label>Prix promo (€)</Label>
                  <Input type="number" step="0.01" value={editing.promo_price || ""} onChange={(e) => setEditing({ ...editing, promo_price: e.target.value })} className="mt-1.5" data-testid="edit-promoprice-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} className="rounded-full" data-testid="save-price-btn">Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
